import React from 'react';
import { FaBox, FaFileAlt, FaMoneyBillWave, FaImage, FaAsterisk } from 'react-icons/fa';
import { Input, TextArea } from '../AdminForm';
import { ProductFormFieldsProps } from './ProductFormTypes';
import { getValidationClassName } from './useProductFormValidation';

/**
 * Product Form Fields Component
 * Renders basic product information fields (name, description, price, image)
 */

const ProductFormFields: React.FC<ProductFormFieldsProps> = ({
  product,
  onUpdate,
  validation,
  errors,
}) => {
  return (
    <>
      {/* Temel Bilgiler */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-5 rounded-xl border border-blue-100">
        <div className="space-y-4">
          {/* Product Name */}
          <div className="relative">
            <Input
              label={
                <span className="flex items-center gap-1">
                  Ürün Adı
                  <FaAsterisk className="text-red-500 text-xs" />
                </span>
              }
              value={product.name || ''}
              onChange={value => onUpdate({ name: value })}
              placeholder="Örn: Earl Grey Çay"
              icon={<FaBox />}
              className={getValidationClassName(validation.isNameValid, !!product.name)}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <FaAsterisk className="text-xs" />
                {errors.name}
              </p>
            )}
          </div>

          {/* Product Description */}
          <div className="relative">
            <TextArea
              label={
                <span className="flex items-center gap-1">
                  Açıklama
                  <FaAsterisk className="text-red-500 text-xs" />
                </span>
              }
              value={product.description || ''}
              onChange={value => onUpdate({ description: value })}
              placeholder="Ürün açıklaması yazın..."
              rows={3}
              icon={<FaFileAlt />}
              className={getValidationClassName(
                validation.isDescriptionValid,
                !!product.description
              )}
            />
            {errors.description && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <FaAsterisk className="text-xs" />
                {errors.description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Fiyat ve Görsel */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-5 rounded-xl border border-green-100">
        <div className="grid grid-cols-1 gap-4">
          {/* Product Price */}
          <div className="relative">
            <Input
              label={
                <span className="flex items-center gap-1">
                  Fiyat (₺)
                  <FaAsterisk className="text-red-500 text-xs" />
                </span>
              }
              type="number"
              value={product.price || ''}
              onChange={value => onUpdate({ price: Number(value) })}
              placeholder="0.00"
              min={0}
              icon={<FaMoneyBillWave />}
              className={getValidationClassName(validation.isPriceValid, !!product.price)}
            />
            {errors.price && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <FaAsterisk className="text-xs" />
                {errors.price}
              </p>
            )}
          </div>

          {/* Product Image URL */}
          <div className="relative">
            <Input
              label="Ürün Resmi URL"
              value={product.image_url || ''}
              onChange={value => onUpdate({ image_url: value })}
              placeholder="https://example.com/image.jpg"
              icon={<FaImage />}
              className={product.image_url ? getValidationClassName(!errors.image_url, true) : ''}
            />
            {errors.image_url && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <FaAsterisk className="text-xs" />
                {errors.image_url}
              </p>
            )}
            {product.image_url && !errors.image_url && (
              <div className="mt-2">
                <div className="flex items-center gap-2 text-xs text-green-600">
                  <FaImage />
                  <span>Görsel URL'si geçerli görünüyor</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductFormFields;
